import { Box, ClickAwayListener, Popper } from "@mui/material";
import { useRef, useState } from "react";

import { trackEvent } from "utils/tracking";

import styles from "./styles";

const DELAY_OPTIONS = [
  { label: "1 day", value: 1 },
  { label: "2 days", value: 2 },
  { label: "3 days", value: 3 },
  { label: "4 days", value: 4 },
  { label: "5 days", value: 5 },
  { label: "7 days", value: 7 },
  { label: "10 days", value: 10 },
  { label: "14 days", value: 14 },
];

interface Props {
  days: number;
  onChange: (value: number) => void;
  isFirst?: boolean;
  align?: "center" | "start";
}

export default function FollowUpDelayDivider({
  days,
  onChange,
  isFirst = false,
  align = "center",
}: Props) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "start" ? "flex-start" : "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {!isFirst && <Box sx={styles.dashed} />}
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Box
            ref={anchorRef}
            component="button"
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            sx={styles.dayContainer}
          >
            <svg
              width="9"
              height="9"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 4.5V8l2.5 1.5" />
            </svg>
            {days}d
            <svg width="7" height="7" viewBox="0 0 12 12" fill="currentColor">
              <path
                d="M3 5l3 3 3-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          {isFirst && (
            <Box component="span" sx={styles.businessText}>
              business days until the next follow-up is sent
            </Box>
          )}
        </Box>
        <Box sx={styles.dashed} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="right-start"
        disablePortal={false}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              background: "#fff",
              border: "1px solid #e0e2e0",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              minWidth: 100,
              padding: "4px 0",
            }}
          >
            {DELAY_OPTIONS.map((opt) => {
              const selected = opt.value === days;
              return (
                <Box
                  key={opt.value}
                  component="button"
                  type="button"
                  onClick={() => {
                    trackEvent("Follow-Up Delay Changed", { delay: opt.value });
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  sx={styles.label(selected)}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
